import React, { useState } from "react";

import { IDog, IDogRun } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import Button from "../../../components/Button/Button";
import Checkbox from "../../../components/Checkbox/Checkbox";
import DatePick from "../../../components/DatePick/DatePick";
import Dropdown from "../../../components/Dropdown/Dropdown";
import Input from "../../../components/Input/Input";
import TextArea from "../../../components/TextArea/TextArea";

import { formatRunDate, parseRunDate, runOption, runOptions } from "./runFormFields";

import "./UKCObedienceRunForm.scss";

interface IProps {
  dog: IDog;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  editRun?: IDogRun;
}

const UKC_OBEDIENCE_CLASSES = [
  { value: 'Novice', label: 'Novice' },
  { value: 'Open A', label: 'Open A' },
  { value: 'Open B', label: 'Open B' },
  { value: 'Utility A', label: 'Utility A' },
  { value: 'Utility B', label: 'Utility B' }
];

const RESULT_STATUSES = [
  { value: Constants.run_result_status.qualified, label: 'Q (Qualified)' },
  { value: Constants.run_result_status.not_qualified, label: 'NQ (Not Qualified)' },
  { value: Constants.run_result_status.absent, label: 'ABS (Absent)' }
];

const UKC_AWARDS = [
  { value: Constants.ukc_obedience_award.high_in_trial, label: 'High in Trial (HIT)' },
  { value: Constants.ukc_obedience_award.high_combined, label: 'High Combined (HC)' }
];

const UKCObedienceRunForm: React.FC<IProps> = ({ dog, onSubmit, onCancel, isSubmitting, editRun }) => {
  // Form fields
  const [trialDate, setTrialDate] = useState<string>(
    editRun?.trial_date ? new Date(editRun.trial_date).toISOString().split('T')[0] : ''
  );
  const [obedienceClass, setObedienceClass] = useState<string>(editRun?.class || '');
  const [score, setScore] = useState<string>(editRun?.score?.toString() || '');
  const [resultStatus, setResultStatus] = useState<string>(editRun?.result_status || '');
  const [placement, setPlacement] = useState<string>(editRun?.placement?.toString() || '');
  const [dogsDefeated, setDogsDefeated] = useState<string>(editRun?.dogs_defeated?.toString() || '');
  const [awards, setAwards] = useState<string[]>(editRun?.awards || []);
  const [judgeNumber, setJudgeNumber] = useState<string>(editRun?.judge_number || '');
  const [judgeName, setJudgeName] = useState<string>(editRun?.judge_name || '');
  const [eventName, setEventName] = useState<string>(editRun?.event_name || '');
  const [city, setCity] = useState<string>(editRun?.location?.city || '');
  const [state, setState] = useState<string>(editRun?.location?.state || '');
  const [notes, setNotes] = useState<string>(editRun?.notes || '');

  const handleAwardToggle = (awardValue: string) => {
    setAwards(prev => {
      if (prev.includes(awardValue)) {
        return prev.filter(a => a !== awardValue);
      } else {
        return [...prev, awardValue];
      }
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Parse the date and set it to noon local time to avoid timezone issues
    const [year, month, day] = trialDate.split('-').map(Number);
    const localDate = new Date(year, month - 1, day, 12, 0, 0);
    
    const data: any = {
      dogId: dog._id,
      obedienceClass,
      score: parseFloat(score),
      resultStatus,
      trialDate: localDate,
      placement: placement ? parseInt(placement, 10) : undefined,
      dogsDefeated: dogsDefeated ? parseInt(dogsDefeated, 10) : undefined,
      awards: awards.length > 0 ? awards : undefined,
      judgeNumber: judgeNumber || undefined,
      judgeName: judgeName || undefined,
      eventName: eventName || undefined,
      location: (city || state) ? { city, state } : undefined,
      notes: notes || undefined
    };

    if (editRun) {
      data.run_id = editRun._id;
    }

    onSubmit(data);
  };

  const classOptions = runOptions(UKC_OBEDIENCE_CLASSES, "Select Class");
  const resultOptions = runOptions(RESULT_STATUSES, "Select Result");
  const isValid = trialDate && obedienceClass && score && resultStatus;

  return (
    <form onSubmit={handleSubmit} className="ukcObedienceRunForm">
      <div className="formGrid">
        <div className="fieldGroup">
          <DatePick
            label="Trial Date *"
            placeholder="Select date"
            value={parseRunDate(trialDate)}
            onChange={(date: Date | null) => setTrialDate(formatRunDate(date))}
          />
        </div>

        <div className="fieldGroup">
          <Dropdown
            label="Class *"
            placeholder="Select Class"
            options={classOptions}
            value={runOption(classOptions, obedienceClass)}
            onChange={(_e, option) => setObedienceClass(option.id)}
          />
        </div>

        <div className="fieldGroup">
          <Input
            label="Score (out of 200) *"
            id="score"
            type="number"
            min="0"
            max="200"
            step="0.5"
            value={score}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setScore(e.target.value)}
            placeholder="0-200 (half points allowed)"
            required
          />
          <span className="fieldHelp">
            Minimum 170 required for qualification. Half points allowed (e.g., 185.5)
          </span>
        </div>

        <div className="fieldGroup">
          <Dropdown
            label="Result *"
            placeholder="Select Result"
            options={resultOptions}
            value={runOption(resultOptions, resultStatus)}
            onChange={(_e, option) => setResultStatus(option.id)}
          />
        </div>

        <div className="fieldGroup">
          <Input
            label="Placement"
            id="placement"
            type="number"
            min="1"
            value={placement}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPlacement(e.target.value)}
            placeholder="Optional (1st, 2nd, etc.)"
          />
          <span className="fieldHelp">
            Required for championship points
          </span>
        </div>

        <div className="fieldGroup">
          <Input
            label="Dogs Defeated"
            id="dogsDefeated"
            type="number"
            min="0"
            value={dogsDefeated}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDogsDefeated(e.target.value)}
            placeholder="Optional"
          />
          <span className="fieldHelp">
            Required for championship points
          </span>
        </div>

        <div className="fieldGroup fullWidth">
          <span className="groupLabel">Awards</span>
          <div className="checkboxGroup">
            {UKC_AWARDS.map(award => (
              <Checkbox
                key={award.value}
                id={`ukc-obedience-award-${award.value}`}
                label={award.label}
                value={awards.includes(award.value)}
                onChange={() => handleAwardToggle(award.value)}
              />
            ))}
          </div>
          <span className="fieldHelp">
            Required for championship titles (UOCH, UGOCH)
          </span>
        </div>

        <div className="fieldGroup">
          <Input
            label="Judge Number"
            id="judgeNumber"
            value={judgeNumber}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setJudgeNumber(e.target.value)}
            placeholder="Optional"
          />
          <span className="fieldHelp">
            Preferred for uniqueness
          </span>
        </div>

        <div className="fieldGroup">
          <Input
            label="Judge Name"
            id="judgeName"
            value={judgeName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setJudgeName(e.target.value)}
            placeholder="Optional"
          />
          <span className="fieldHelp">
            Used as fallback if judge number not available
          </span>
        </div>

        <div className="fieldGroup">
          <Input
            label="Event Name"
            id="eventName"
            value={eventName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEventName(e.target.value)}
            placeholder="Optional"
          />
        </div>

        <div className="fieldGroup">
          <Input
            label="City"
            id="city"
            value={city}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCity(e.target.value)}
            placeholder="Optional"
          />
        </div>

        <div className="fieldGroup">
          <Input
            label="State"
            id="state"
            value={state}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setState(e.target.value)}
            placeholder="Optional"
          />
        </div>

        <div className="fieldGroup fullWidth">
          <TextArea
            className="runNotes"
            label="Notes"
            placeholder="Optional notes about this run"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>

      {/* Form Actions */}
      <div className="formActions">
        <Button
          onClick={onCancel}
          variant="secondary"
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="default"
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? "Saving..." : editRun ? "Update Run" : "Add Run"}
        </Button>
      </div>
    </form>
  );
};

export default UKCObedienceRunForm;

